import type { AbilityId } from '../abilities/AbilityDefs';
import type { StatModifier } from '../stats/StatSheet';
import type { ProfileData } from '../meta/Profile';
import type { AspectId } from './aspects';

/**
 * The roster (Depths rework). Each character locks a signature skill into
 * slot 0 — the build's spine — but the ASPECT carries the identity: a rule
 * that changes what your hands do, never a stat slider in a costume. The
 * weapon is effectively the character's tutorial.
 *
 * Hale (#0) is the deliberate exception: the legible starter with NO aspect
 * and no stat variance — the character you learn "normal" on.
 *
 * Melee's bribe: with auto-fire, short range is strictly worse unless
 * proximity pays (Barrow's Ironbound, Thirl's drink). The real axis is
 * engagement distance + risk posture, not melee/ranged.
 *
 * Free roster: Hale, Wick, Barrow (ranged + melee coverage, simplest
 * aspects). The rest are milestone-gated (milestones.ts).
 */

export type CharacterId =
  | 'hale' | 'wick' | 'mourne' | 'vane' | 'barrow' | 'trine'
  | 'bramGaunt' | 'cask' | 'thirl';

export interface CharacterDef {
  id: CharacterId;
  name: string;
  /** Picker glyph. */
  short: string;
  desc: string;
  /** Locked into slot 0; never offered as a NEW skill (its upgrade is). */
  signatureAbility: AbilityId;
  /** The identity rule (aspects.ts); null only for the starter. */
  aspect: AspectId | null;
  aspectName: string | null;
  aspectBlurb: string | null;
  /** Applied at run start under source 'character'. */
  statMods: StatModifier[];
  /** Milestone gate: null = always available. */
  unlock: keyof ProfileData['unlocks'] | null;
  /** Shown on locked picker chips ("Unlock: <hint>"). */
  unlockHint: string | null;
}

export const CHARACTERS: Record<CharacterId, CharacterDef> = {
  hale: {
    id: 'hale',
    name: 'Hale, the Steady',
    short: 'H',
    desc: 'A steady hand and a plain sling. Nothing strange about him — that\'s the point.',
    signatureAbility: 'lodestar',
    aspect: null,
    aspectName: null,
    aspectBlurb: null,
    statMods: [],
    unlock: null,
    unlockHint: null,
  },
  wick: {
    id: 'wick',
    name: 'Wick, the Cinderpriest',
    short: 'W',
    desc: 'Burns brightest at the bottom of the wick.',
    signatureAbility: 'censer',
    aspect: 'guttering',
    aspectName: 'Guttering',
    aspectBlurb: 'His candle burns down over the floor and his damage rises '
      + 'as it dies — at empty, it starts burning HIM. Descend to relight it; '
      + 'chests and shrines buy seconds back.',
    statMods: [],
    unlock: null,
    unlockHint: null,
  },
  mourne: {
    id: 'mourne',
    name: 'Mourne, the Bonewright',
    short: 'M',
    desc: 'His hands are for gathering; the dead do the killing.',
    signatureAbility: 'rotBloom',
    aspect: 'ossuary',
    aspectName: 'Ossuary',
    aspectBlurb: 'Corpses raise crawlers that do much of his killing, and '
      + 'spare corpses bank into a reserve that survives descent. Keep the '
      + 'swarm fed — the reserve fuels it through boss fights.',
    // M0 retune: was −50% when crawlers bypassed damageMult entirely (the tax
    // only hit his picks). Bites ride the funnel now, so the tax reaches the
    // swarm too — softened so leveling still feels like it does something.
    statMods: [{ stat: 'damageMult', pct: -0.3 }],
    unlock: 'charMourne',
    unlockHint: 'slay 3,000 foes',
  },
  vane: {
    id: 'vane',
    name: 'Vane, the Knifedancer',
    short: 'V',
    desc: 'Speed is ammunition.',
    signatureAbility: 'fanKnives',
    aspect: 'ninefold',
    aspectName: 'Ninefold',
    aspectBlurb: 'Once per floor she refuses a death outright. Nothing can '
      + 'heal her mid-floor — descending is her only mending: it makes her '
      + 'whole and refreshes the refusal. Spend it, fight hot, get down.',
    statMods: [{ stat: 'moveSpeed', pct: 0.08 }],
    unlock: 'charVane',
    unlockHint: 'reach floor 3',
  },
  barrow: {
    id: 'barrow',
    name: 'Barrow, the Warden',
    short: 'B',
    desc: 'Too slow to leave. Never wanted to.',
    signatureAbility: 'cleave',
    aspect: 'ironbound',
    aspectName: 'Ironbound',
    aspectBlurb: 'Every hit he takes stokes a burning aura around him. '
      + 'Stay in the scrum: pain is his damage, and walking away wastes it.',
    statMods: [
      { stat: 'maxHp', flat: 2 },
      { stat: 'moveSpeed', pct: -0.12 },
      // M0 (feel pass): the close-combat pick swings faster — he can't leave
      // the fight, so he ends it sooner. Rides every cooldown, cleave included.
      { stat: 'cooldownMult', pct: -0.12 },
    ],
    unlock: null,
    unlockHint: null,
  },
  trine: {
    id: 'trine',
    name: 'Trine, the Prism-conjurer',
    short: 'T',
    desc: 'Three of her is a problem for everyone.',
    signatureAbility: 'prismLance',
    aspect: 'sunder',
    aspectName: 'Sunder',
    aspectBlurb: 'Every 12 seconds she splits for 4: untouchable and casting '
      + 'three times as fast, but unable to pick anything up. Position during '
      + 'the countdown, dive when the dial fills.',
    statMods: [],
    unlock: 'charTrine',
    unlockHint: 'win 3 runs',
  },
  bramGaunt: {
    id: 'bramGaunt',
    name: 'Bram & Gaunt, the Houndkeeper',
    short: 'G',
    desc: 'Bram carries the leash. Gaunt carries the floor.',
    signatureAbility: 'slipleash',
    aspect: 'sharedFate',
    aspectName: 'Shared Fate',
    aspectBlurb: 'Gaunt fights at Bram\'s side and vacuums gems at huge '
      + 'range — but when he falls, the floor\'s unbanked gems die with him. '
      + 'Kneel at the body to bring him back, at a price.',
    statMods: [],
    unlock: 'charBramGaunt',
    unlockHint: 'bank 8 artifacts',
  },
  cask: {
    id: 'cask',
    name: 'Cask, the Powdermonk',
    short: 'C',
    desc: 'Patience is for people without powder.',
    signatureAbility: 'boreCharge',
    aspect: 'shortcut',
    aspectName: 'Shortcut',
    aspectBlurb: 'He can fuse-blast the way down open anywhere, skipping the '
      + 'rest of the floor. His own powder never kills — it scorches his max '
      + 'health instead, and descending heals every burn.',
    statMods: [{ stat: 'maxHp', flat: 1 }],
    unlock: 'charCask',
    unlockHint: 'win a run',
  },
  thirl: {
    id: 'thirl',
    name: 'Thirl, the Bloodbound',
    short: 'R',
    desc: 'The quiet moments are the ones that kill him.',
    signatureAbility: 'leechChain',
    aspect: 'ebb',
    aspectName: 'Ebb',
    aspectBlurb: 'His health always drains — three times as fast when his '
      + 'chain isn\'t drinking. The thick of the fight is the only safe '
      + 'place he has.',
    statMods: [{ stat: 'maxHp', flat: 2 }],
    unlock: 'charThirl',
    unlockHint: 'die 10 times',
  },
};

export const CHARACTER_LIST: CharacterDef[] = Object.values(CHARACTERS);

export const DEFAULT_CHARACTER: CharacterId = 'hale';

export function isCharacterId(v: unknown): v is CharacterId {
  return typeof v === 'string' && v in CHARACTERS;
}

export function isCharacterUnlocked(p: ProfileData, def: CharacterDef): boolean {
  return def.unlock === null || p.unlocks[def.unlock];
}
